import { ChevronDown, Link2, RotateCcw } from "lucide-react";

import {
  buildCollapsedMobileTransaction,
  formatCaixaBalance,
  shouldShowUndoAction,
} from "@/features/caixas/display";
import type { CaixaTransactionRow } from "@/features/caixas/types";
import {
  displayCaixaObservation,
  shortCaixaReference,
} from "./caixaTransactionPresentation";

const mobileDateFormatter = new Intl.DateTimeFormat("pt-BR", {
  dateStyle: "short",
  timeStyle: "short",
});

interface CaixasMobileTransactionRowProps {
  transaction: CaixaTransactionRow;
  isExpanded: boolean;
  onToggle: () => void;
  onUndo: (transaction: CaixaTransactionRow) => void;
  onOpenCarga?: (checkinId: string) => void;
  canExpand?: boolean;
}

function movementLabel(transaction: CaixaTransactionRow) {
  if (transaction.undone_by_transaction_id) {
    return transaction.operation === "CREDIT"
      ? "Entrada desfeita"
      : "Saída desfeita";
  }
  if (transaction.kind === "undo") return "Desfazimento";
  return `${transaction.operation === "CREDIT" ? "Entrada" : "Saída"} ${transaction.checkin_id ? "por carga" : "avulsa"}`;
}

function hasDetails(
  transaction: CaixaTransactionRow,
  onOpenCarga?: (checkinId: string) => void,
) {
  return (
    Boolean(transaction.observation) ||
    Boolean(transaction.undo_of_transaction_id) ||
    Boolean(transaction.undone_by_transaction_id) ||
    Boolean(transaction.checkin_id && onOpenCarga) ||
    shouldShowUndoAction(transaction)
  );
}

export function CaixasMobileTransactionRow({
  transaction,
  isExpanded,
  onToggle,
  onUndo,
  onOpenCarga,
  canExpand,
}: CaixasMobileTransactionRowProps) {
  const collapsed = buildCollapsedMobileTransaction(transaction);
  const expandedId = `caixas-mobile-transaction-${transaction.id}-details`;
  const isExpandable = canExpand ?? hasDetails(transaction, onOpenCarga);
  const amountColor =
    transaction.kind === "undo"
      ? "#b45309"
      : transaction.operation === "CREDIT"
        ? "#16a34a"
        : "#ef4444";

  return (
    <article
      className="caixas-mobile-row"
      data-state={isExpanded ? "open" : "closed"}
    >
      <button
        type="button"
        className="caixas-mobile-row-button"
        onClick={() => isExpandable && onToggle()}
        aria-label={
          isExpandable
            ? isExpanded
              ? "Ocultar detalhes da movimentação"
              : "Mostrar detalhes da movimentação"
            : undefined
        }
        aria-expanded={isExpandable ? isExpanded : undefined}
        aria-controls={isExpandable ? expandedId : undefined}
        style={{
          width: "100%",
          minHeight: "5.1rem",
          padding: ".7rem .35rem .7rem .65rem",
          border: 0,
          background: "transparent",
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) auto 2.25rem",
          alignItems: "center",
          gap: ".55rem",
          cursor: isExpandable ? "pointer" : "default",
          textAlign: "left",
        }}
      >
        <span style={{ minWidth: 0 }}>
          <strong
            style={{
              display: "block",
              color: "var(--login-text)",
              overflowWrap: "anywhere",
            }}
          >
            {movementLabel(transaction)}
          </strong>
          <small
            style={{
              display: "block",
              marginTop: ".25rem",
              color: "var(--login-muted)",
              overflowWrap: "anywhere",
            }}
          >
            {transaction.actor_label}
          </small>
        </span>
        <span style={{ display: "grid", justifyItems: "end", gap: ".2rem" }}>
          <strong
            style={{
              color: amountColor,
              font: "700 1.15rem 'Barlow Condensed', sans-serif",
              whiteSpace: "nowrap",
            }}
          >
            {collapsed.amount}
          </strong>
          <small style={{ color: "var(--login-muted)", whiteSpace: "nowrap" }}>
            {mobileDateFormatter.format(new Date(transaction.created_at))}
          </small>
        </span>
        {isExpandable ? (
          <ChevronDown
            className="caixas-disclosure-chevron"
            size={16}
            aria-hidden="true"
          />
        ) : (
          <span />
        )}
      </button>

      {isExpanded && isExpandable ? (
        <div
          id={expandedId}
          className="caixas-mobile-row-details caixas-detail-grid"
          style={{ padding: ".75rem .75rem .85rem" }}
        >
          <div className="caixas-detail-observation">
            <strong>Observação</strong>
            <p>{displayCaixaObservation(transaction)}</p>
            {transaction.undo_of_transaction_id ? (
              <p>
                Movimentação original{" "}
                {shortCaixaReference(transaction.undo_of_transaction_id)}
              </p>
            ) : null}
          </div>
          <div className="caixas-balance-comparison">
            <span className="caixas-balance-fact">
              <small>Antes</small>
              <strong>
                {formatCaixaBalance(transaction.previous_balance)}
              </strong>
            </span>
            <span className="caixas-balance-fact">
              <small>Depois</small>
              <strong>{formatCaixaBalance(transaction.new_balance)}</strong>
            </span>
          </div>
          <div className="caixas-actions">
            {transaction.checkin_id && onOpenCarga ? (
              <button
                type="button"
                className="caixas-table-action"
                onClick={(event) => {
                  event.stopPropagation();
                  onOpenCarga(transaction.checkin_id!);
                }}
              >
                <Link2 size={14} />
                Abrir carga
              </button>
            ) : null}
            {shouldShowUndoAction(transaction) ? (
              <button
                type="button"
                className="caixas-table-action caixas-undo-action"
                aria-label="Desfazer movimentação"
                onClick={(event) => {
                  event.stopPropagation();
                  onUndo(transaction);
                }}
              >
                <RotateCcw size={14} />
                Desfazer
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
    </article>
  );
}
